import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Target, TrendingUp, Plus, ChevronLeft, LogOut, Edit, Trash2, 
  DollarSign, Calendar, MoreHorizontal, Loader2, Check, Pause, X,
  Settings, Home, PiggyBank, RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/context/BrandContext";
import { useLanguage } from "@/context/LanguageContext";
import api from "@/lib/api";
import { format, parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

// Helper to format dates in UTC
const formatDateUTC = (dateString, formatStr = "MMM d, yyyy") => {
  if (!dateString) return "-";
  try {
    return formatInTimeZone(parseISO(dateString), "UTC", formatStr);
  } catch {
    return format(parseISO(dateString), formatStr);
  }
};

export default function GoalsPage() {
  const { user, logout } = useAuth();
  const { brandName } = useBrand();
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  const [goals, setGoals] = useState([]);
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [userSettings, setUserSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  const [showGoalDialog, setShowGoalDialog] = useState(false);
  const [showContributionDialog, setShowContributionDialog] = useState(false);
  const [showContributionsListDialog, setShowContributionsListDialog] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [contributions, setContributions] = useState([]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [summaryRes, categoriesRes, currenciesRes, settingsRes] = await Promise.all([
        api.get("/goals/summary"),
        api.get("/goal-categories"),
        api.get("/currencies"),
        api.get("/settings")
      ]);
      setSummary(summaryRes.data);
      setGoals(summaryRes.data.goals || []);
      setCategories(categoriesRes.data);
      setCurrencies(currenciesRes.data);
      setUserSettings(settingsRes.data);
    } catch (error) {
      toast.error("Failed to load goals data");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchContributions = async (goalId) => {
    try {
      const res = await api.get(`/goals/${goalId}/contributions`);
      setContributions(res.data);
    } catch (error) {
      toast.error(t("failed_to_load", "errors"));
    }
  };

  const handleDeleteGoal = async (goalId) => {
    if (!confirm(t("confirm_delete_goal", "goals"))) return;
    try {
      await api.delete(`/goals/${goalId}`);
      toast.success(t("goal_deleted", "goals"));
      fetchData();
    } catch (error) {
      toast.error(t("failed_to_delete", "errors"));
    }
  };

  const handleUpdateStatus = async (goalId, newStatus) => {
    try {
      await api.patch(`/goals/${goalId}/status?status=${newStatus}`);
      toast.success(t("goal_updated", "goals"));
      fetchData();
    } catch (error) {
      toast.error(t("failed_to_save", "errors"));
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success(t("logged_out", "dashboard"));
  };

  const getCurrencySymbol = (code) => {
    const currency = currencies.find(c => c.code === code);
    return currency?.symbol || code;
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: "bg-[hsl(var(--ff-success)/.15)] text-[hsl(var(--ff-success))]",
      completed: "bg-[hsl(var(--ff-info)/.15)] text-[hsl(var(--ff-info))]",
      paused: "bg-[hsl(var(--ff-warning)/.15)] text-[hsl(var(--ff-warning))]",
      cancelled: "bg-muted text-muted-foreground"
    };
    return styles[status] || styles.active;
  };

  const getCategoryIcon = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.icon || "🎯";
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-[1400px] mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="size-8 rounded-lg bg-[hsl(var(--ff-primary))] flex items-center justify-center">
              <Target className="h-5 w-5 text-white" />
            </div>
            <span className="font-semibold text-lg">{brandName}</span>
            <Badge variant="outline" className="ml-2">{t("goals", "goals")}</Badge>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard">
              <Home className="h-4 w-4 mr-2" /> {t("dashboard", "dashboard")}
            </Button>
            <Button onClick={() => { setEditingGoal(null); setShowGoalDialog(true); }} data-testid="add-goal-button">
              <Plus className="h-4 w-4 mr-2" /> {t("add_goal", "goals")}
            </Button>
            <Button variant="ghost" size="icon" onClick={() => navigate("/settings")} data-testid="settings-button">
              <Settings className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-button">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1400px] mx-auto px-6 py-6 space-y-6">
        {/* Summary Cards */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i}>
                <CardContent className="p-6">
                  <Skeleton className="h-4 w-20 mb-2" />
                  <Skeleton className="h-8 w-32" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="goals-summary-cards">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">{t("total_target", "goals")}</p>
                  <div className="size-8 rounded-full bg-[hsl(var(--ff-primary)/.1)] flex items-center justify-center">
                    <Target className="h-4 w-4 text-[hsl(var(--ff-primary))]" />
                  </div>
                </div>
                <p className="text-2xl font-bold font-numeric mt-2" data-testid="total-target-value">
                  {getCurrencySymbol(summary?.currency || 'USD')}{summary?.total_target?.toLocaleString() || "0"}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">{t("total_saved", "goals")}</p>
                  <div className="size-8 rounded-full income-bg flex items-center justify-center">
                    <PiggyBank className="h-4 w-4 income-text" />
                  </div>
                </div>
                <p className="text-2xl font-bold font-numeric mt-2 income-text" data-testid="total-saved-value">
                  {getCurrencySymbol(summary?.currency || 'USD')}{summary?.total_saved?.toLocaleString() || "0"}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">{t("overall_progress", "goals")}</p>
                  <div className="size-8 rounded-full bg-[hsl(var(--ff-info)/.1)] flex items-center justify-center">
                    <TrendingUp className="h-4 w-4 text-[hsl(var(--ff-info))]" />
                  </div>
                </div>
                <div className="mt-2">
                  <p className="text-2xl font-bold font-numeric" data-testid="overall-progress-value">
                    {summary?.overall_progress?.toFixed(1) || "0"}%
                  </p>
                  <Progress value={summary?.overall_progress || 0} className="h-2 mt-2" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">{t("goals_status", "goals")}</p>
                  <div className="size-8 rounded-full bg-[hsl(var(--ff-warning)/.1)] flex items-center justify-center">
                    <DollarSign className="h-4 w-4 text-[hsl(var(--ff-warning))]" />
                  </div>
                </div>
                <div className="flex gap-3 mt-2">
                  <Badge variant="outline" className="bg-[hsl(var(--ff-success)/.15)] text-[hsl(var(--ff-success))]" data-testid="active-goals-badge">
                    {summary?.active_goals || 0} {t("active", "goals")}
                  </Badge>
                  <Badge variant="outline" className="bg-[hsl(var(--ff-info)/.15)] text-[hsl(var(--ff-info))]" data-testid="completed-goals-badge">
                    {summary?.completed_goals || 0} {t("done", "goals")}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Actions Row */}
        <div className="flex gap-3">
          <Button variant="outline" onClick={fetchData} data-testid="refresh-goals-button">
            <RefreshCw className="h-4 w-4 mr-2" /> {t("refresh", "common")}
          </Button>
        </div>

        {/* Goals Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <Card key={i}>
                <CardContent className="p-6">
                  <Skeleton className="h-6 w-40 mb-4" />
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-8 w-32" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : goals.length === 0 ? (
          <Card className="p-12">
            <div className="text-center">
              <Target className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-xl font-semibold mb-2">{t("no_goals_yet", "goals")}</h3>
              <p className="text-muted-foreground mb-6">{t("start_first_goal", "goals")}</p>
              <Button onClick={() => { setEditingGoal(null); setShowGoalDialog(true); }} data-testid="empty-add-goal-button">
                <Plus className="h-4 w-4 mr-2" /> {t("create_goal", "goals")}
              </Button>
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="goals-grid">
            {goals.map((goal) => (
              <Card 
                key={goal.id} 
                className={`relative overflow-hidden transition-all hover:shadow-lg ${goal.status === 'completed' ? 'border-[hsl(var(--ff-success)/.5)]' : ''} ${goal.status === 'paused' ? 'opacity-75' : ''} ${goal.status === 'cancelled' ? 'opacity-50' : ''}`}
                data-testid={`goal-card-${goal.id}`}
              >
                {/* Color accent bar */}
                <div 
                  className="absolute top-0 left-0 right-0 h-1" 
                  style={{ backgroundColor: goal.color || 'hsl(var(--ff-primary))' }}
                />
                
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{goal.icon || getCategoryIcon(goal.category)}</span>
                      <div>
                        <CardTitle className="text-lg">{goal.name}</CardTitle>
                        {goal.category_name && (
                          <CardDescription>{goal.category_name}</CardDescription>
                        )}
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`goal-menu-${goal.id}`}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => { setEditingGoal(goal); setShowGoalDialog(true); }}>
                          <Edit className="h-4 w-4 mr-2" /> {t("edit", "common")}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => { setSelectedGoal(goal); fetchContributions(goal.id); setShowContributionsListDialog(true); }}>
                          <DollarSign className="h-4 w-4 mr-2" /> {t("view_contributions", "goals")}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {goal.status === 'active' && (
                          <>
                            <DropdownMenuItem onClick={() => handleUpdateStatus(goal.id, 'completed')}>
                              <Check className="h-4 w-4 mr-2" /> {t("mark_complete", "goals")}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleUpdateStatus(goal.id, 'paused')}>
                              <Pause className="h-4 w-4 mr-2" /> {t("pause", "goals")}
                            </DropdownMenuItem>
                          </>
                        )}
                        {goal.status === 'paused' && (
                          <DropdownMenuItem onClick={() => handleUpdateStatus(goal.id, 'active')}>
                            <RefreshCw className="h-4 w-4 mr-2" /> {t("resume", "goals")}
                          </DropdownMenuItem>
                        )}
                        {goal.status === 'completed' && (
                          <DropdownMenuItem onClick={() => handleUpdateStatus(goal.id, 'active')}>
                            <RefreshCw className="h-4 w-4 mr-2" /> {t("reopen", "goals")}
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={() => handleDeleteGoal(goal.id)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" /> {t("delete", "common")}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  {/* Progress */}
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-muted-foreground">{t("progress", "goals")}</span>
                      <span className="font-medium">{goal.progress_percentage}%</span>
                    </div>
                    <Progress 
                      value={goal.progress_percentage} 
                      className={`h-3 ${goal.status === 'completed' ? '[&>div]:bg-[hsl(var(--ff-success))]' : ''}`}
                    />
                  </div>
                  
                  {/* Amount */}
                  <div className="flex justify-between items-baseline">
                    <div>
                      <p className="text-2xl font-bold font-numeric income-text">
                        {getCurrencySymbol(goal.currency)}{goal.current_saved.toLocaleString()}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {t("target", "goals")}: {getCurrencySymbol(goal.currency)}{goal.target_amount.toLocaleString()}
                      </p>
                    </div>
                    <Badge className={getStatusBadge(goal.status)}>
                      {t(goal.status, "goals")}
                    </Badge>
                  </div>
                  
                  {/* Target Date */}
                  {goal.target_date && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Calendar className="h-4 w-4" />
                      <span>{t("target_date", "goals")}: {formatDateUTC(goal.target_date)}</span>
                    </div>
                  )}
                  
                  {/* Description */}
                  {goal.description && (
                    <p className="text-sm text-muted-foreground line-clamp-2">{goal.description}</p>
                  )}
                  
                  {/* Add Contribution Button */}
                  {goal.status === 'active' && (
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => { setSelectedGoal(goal); setShowContributionDialog(true); }}
                      data-testid={`add-contribution-${goal.id}`}
                    >
                      <Plus className="h-4 w-4 mr-2" /> {t("add_contribution", "goals")}
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Goal Dialog */}
      <GoalDialog
        open={showGoalDialog}
        onOpenChange={(open) => { setShowGoalDialog(open); if (!open) setEditingGoal(null); }}
        goal={editingGoal}
        categories={categories}
        currencies={currencies}
        userSettings={userSettings}
        onSuccess={() => { fetchData(); setEditingGoal(null); }}
      />

      {/* Contribution Dialog */}
      <ContributionDialog
        open={showContributionDialog}
        onOpenChange={(open) => { setShowContributionDialog(open); if (!open) setSelectedGoal(null); }}
        goal={selectedGoal}
        currencies={currencies}
        userSettings={userSettings}
        onSuccess={() => { fetchData(); setSelectedGoal(null); }}
      />

      {/* Contributions List Dialog */}
      <ContributionsListDialog
        open={showContributionsListDialog}
        onOpenChange={(open) => { setShowContributionsListDialog(open); if (!open) { setSelectedGoal(null); setContributions([]); } }}
        goal={selectedGoal}
        contributions={contributions}
        currencies={currencies}
        onDelete={async (id) => {
          try {
            await api.delete(`/contributions/${id}`);
            toast.success(t("contribution_deleted", "goals"));
            fetchData();
            if (selectedGoal) fetchContributions(selectedGoal.id);
          } catch (error) {
            toast.error(t("failed_to_delete", "errors"));
          }
        }}
      />
    </div>
  );
}

// Goal Create/Edit Dialog
function GoalDialog({ open, onOpenChange, goal, categories, currencies, userSettings, onSuccess }) {
  const { t } = useLanguage();
  const isEditing = !!goal;
  const [isLoading, setIsLoading] = useState(false);
  const defaultCurrency = userSettings?.default_currency || "USD";
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    target_amount: "",
    currency: defaultCurrency,
    target_date: "",
    category: "",
    icon: "",
    color: "#2892a6",
    priority: 0
  });

  useEffect(() => {
    if (goal) {
      setFormData({
        name: goal.name || "",
        description: goal.description || "",
        target_amount: goal.target_amount?.toString() || "",
        currency: goal.currency || defaultCurrency,
        target_date: goal.target_date ? goal.target_date.split("T")[0] : "",
        category: goal.category || "",
        icon: goal.icon || "",
        color: goal.color || "#2892a6",
        priority: goal.priority || 0
      });
    } else {
      setFormData({
        name: "",
        description: "",
        target_amount: "",
        currency: defaultCurrency,
        target_date: "",
        category: "",
        icon: "",
        color: "#2892a6",
        priority: 0
      });
    }
  }, [goal, open, defaultCurrency]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.target_amount) {
      toast.error(t("required", "common"));
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        ...formData,
        target_amount: parseFloat(formData.target_amount),
        target_date: formData.target_date ? new Date(formData.target_date).toISOString() : null,
        category: formData.category || null,
        icon: formData.icon || null,
        priority: parseInt(formData.priority) || 0
      };

      if (isEditing) {
        await api.put(`/goals/${goal.id}`, payload);
        toast.success(t("goal_updated", "goals"));
      } else {
        await api.post("/goals", payload);
        toast.success(t("goal_created", "goals"));
      }
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || t("failed_to_save", "errors"));
    } finally {
      setIsLoading(false);
    }
  };

  // Get icon for selected category
  const selectedCategory = categories.find(c => c.id === formData.category);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="goal-dialog">
        <DialogHeader>
          <DialogTitle>{isEditing ? t("edit_goal", "goals") : t("create_goal", "goals")}</DialogTitle>
          <DialogDescription>
            {isEditing ? t("update_goal_desc", "goals") : t("set_goal_desc", "goals")}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div className="space-y-2">
            <Label>{t("goal_name", "goals")} *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder={t("goal_name_placeholder", "goals")}
              data-testid="goal-name-input"
              required
            />
          </div>

          {/* Category */}
          <div className="space-y-2">
            <Label>{t("category", "dashboard")}</Label>
            <Select value={formData.category || "none"} onValueChange={(value) => {
              const cat = categories.find(c => c.id === value);
              setFormData({ 
                ...formData, 
                category: value === "none" ? "" : value,
                icon: cat?.icon || formData.icon
              });
            }}>
              <SelectTrigger data-testid="goal-category-select">
                <SelectValue placeholder={t("select_category", "dashboard")}>
                  {selectedCategory ? (
                    <span>{selectedCategory.icon} {selectedCategory.name}</span>
                  ) : (
                    t("select_category", "dashboard")
                  )}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t("no_category", "dashboard")}</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.icon} {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Target Amount & Currency */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("target_amount", "goals")} *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.target_amount}
                onChange={(e) => setFormData({ ...formData, target_amount: e.target.value })}
                placeholder="0.00"
                data-testid="goal-amount-input"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>{t("currency", "dashboard")}</Label>
              <Select
                value={formData.currency}
                onValueChange={(value) => setFormData({ ...formData, currency: value })}
              >
                <SelectTrigger data-testid="goal-currency-select">
                  <SelectValue placeholder={t("currency", "dashboard")} />
                </SelectTrigger>
                <SelectContent>
                  {(userSettings?.enabled_currencies || ["USD"]).map((code) => {
                    const curr = currencies.find(c => c.code === code);
                    return (
                      <SelectItem key={code} value={code}>
                        {curr?.symbol || code} {code}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Target Date */}
          <div className="space-y-2">
            <Label>{t("target_date", "goals")} ({t("optional", "common")})</Label>
            <Input
              type="date"
              value={formData.target_date}
              onChange={(e) => setFormData({ ...formData, target_date: e.target.value })}
              data-testid="goal-date-input"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label>{t("description", "dashboard")} ({t("optional", "common")})</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder={t("goal_description_placeholder", "goals")}
              rows={2}
              data-testid="goal-description-input"
            />
          </div>

          {/* Color */}
          <div className="space-y-2">
            <Label>{t("accent_color", "goals")}</Label>
            <div className="flex gap-2">
              {["#2892a6", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#06b6d4"].map((color) => (
                <button
                  key={color}
                  type="button"
                  className={`w-8 h-8 rounded-full border-2 transition-transform ${formData.color === color ? 'border-foreground scale-110' : 'border-transparent'}`}
                  style={{ backgroundColor: color }}
                  onClick={() => setFormData({ ...formData, color })}
                />
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t("cancel", "common")}
            </Button>
            <Button type="submit" disabled={isLoading} data-testid="goal-submit-button">
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {isEditing ? t("update_goal", "goals") : t("create_goal", "goals")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Contribution Dialog
function ContributionDialog({ open, onOpenChange, goal, currencies, userSettings, onSuccess }) {
  const { t } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const defaultCurrency = goal?.currency || userSettings?.default_currency || "USD";
  
  const [formData, setFormData] = useState({
    amount: "",
    currency: defaultCurrency,
    note: "",
    date: new Date().toISOString().split("T")[0]
  });

  useEffect(() => {
    if (goal) {
      setFormData({
        amount: "",
        currency: goal.currency || defaultCurrency,
        note: "",
        date: new Date().toISOString().split("T")[0]
      });
    }
  }, [goal, open, defaultCurrency]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.amount || !goal) {
      toast.error(t("required", "common"));
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        note: formData.note || null,
        date: new Date(formData.date).toISOString()
      };

      await api.post(`/goals/${goal.id}/contributions`, payload);
      toast.success(t("contribution_added", "goals"));
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || t("failed_to_save", "errors"));
    } finally {
      setIsLoading(false);
    }
  };

  const getCurrencySymbol = (code) => {
    const currency = currencies.find(c => c.code === code);
    return currency?.symbol || code;
  };

  if (!goal) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="contribution-dialog">
        <DialogHeader>
          <DialogTitle>{t("add_contribution", "goals")}</DialogTitle>
          <DialogDescription>
            {t("add_savings_towards", "goals")} "{goal.name}"
          </DialogDescription>
        </DialogHeader>
        
        {/* Goal Progress Info */}
        <div className="bg-muted p-4 rounded-lg space-y-2">
          <div className="flex justify-between text-sm">
            <span>{t("current_progress", "goals")}</span>
            <span className="font-medium">{goal.progress_percentage}%</span>
          </div>
          <Progress value={goal.progress_percentage} className="h-2" />
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>{getCurrencySymbol(goal.currency)}{goal.current_saved.toLocaleString()} {t("saved", "goals")}</span>
            <span>{getCurrencySymbol(goal.currency)}{goal.remaining_amount.toLocaleString()} {t("remaining", "goals")}</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Amount & Currency */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("amount", "dashboard")} *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                placeholder="0.00"
                data-testid="contribution-amount-input"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>{t("currency", "dashboard")}</Label>
              <Select
                value={formData.currency}
                onValueChange={(value) => setFormData({ ...formData, currency: value })}
              >
                <SelectTrigger data-testid="contribution-currency-select">
                  <SelectValue placeholder={t("currency", "dashboard")} />
                </SelectTrigger>
                <SelectContent>
                  {(userSettings?.enabled_currencies || ["USD"]).map((code) => {
                    const curr = currencies.find(c => c.code === code);
                    return (
                      <SelectItem key={code} value={code}>
                        {curr?.symbol || code} {code}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Date */}
          <div className="space-y-2">
            <Label>{t("date", "dashboard")}</Label>
            <Input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              data-testid="contribution-date-input"
              required
            />
          </div>

          {/* Note */}
          <div className="space-y-2">
            <Label>{t("note", "goals")} ({t("optional", "common")})</Label>
            <Input
              value={formData.note}
              onChange={(e) => setFormData({ ...formData, note: e.target.value })}
              placeholder={t("note_placeholder", "goals")}
              data-testid="contribution-note-input"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t("cancel", "common")}
            </Button>
            <Button type="submit" disabled={isLoading} data-testid="contribution-submit-button">
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {t("add_contribution", "goals")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Contributions List Dialog
function ContributionsListDialog({ open, onOpenChange, goal, contributions, currencies, onDelete }) {
  const { t } = useLanguage();
  const getCurrencySymbol = (code) => {
    const currency = currencies.find(c => c.code === code);
    return currency?.symbol || code;
  };

  if (!goal) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="contributions-list-dialog">
        <DialogHeader>
          <DialogTitle>{t("contributions_for", "goals")} "{goal.name}"</DialogTitle>
          <DialogDescription>
            {contributions.length} {t("contributions", "goals")} • {t("total", "dashboard")}: {getCurrencySymbol(goal.currency)}{goal.current_saved.toLocaleString()}
          </DialogDescription>
        </DialogHeader>
        
        {contributions.length === 0 ? (
          <div className="text-center py-8">
            <PiggyBank className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">{t("no_contributions", "goals")}</p>
          </div>
        ) : (
          <div className="max-h-[400px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("date", "dashboard")}</TableHead>
                  <TableHead className="text-right">{t("amount", "dashboard")}</TableHead>
                  <TableHead>{t("note", "goals")}</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contributions.map((contrib) => (
                  <TableRow key={contrib.id}>
                    <TableCell>{formatDateUTC(contrib.date)}</TableCell>
                    <TableCell className="text-right font-numeric font-medium income-text">
                      +{getCurrencySymbol(contrib.currency)}{contrib.amount.toLocaleString()}
                      {contrib.converted_amount && contrib.currency !== goal.currency && (
                        <span className="text-xs text-muted-foreground ml-1">
                          ({getCurrencySymbol(goal.currency)}{contrib.converted_amount.toFixed(2)})
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[150px] truncate" title={contrib.note}>
                      {contrib.note || <span className="text-muted-foreground">-</span>}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => onDelete(contrib.id)}
                        data-testid={`delete-contribution-${contrib.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("close", "common")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
